/* ==========================================================================
   assets/nb-menu.js — Nuttet Baby
   Kilde: design_handoff_shopify/design/Header.dc.html (logikklassen nederst)
          + BEHAVIOR.md afsnit 5 (mega-menu) og 9 (modaler).
   Markup: sections/header.liquid.

   Indhold:
     1) Hjaelpere            — fokusering, scroll-lock (refcount), pointer-type
     2) window.NBModal       — generisk modal/drawer-hjaelper
                               { open(el), close(el), trapFocus(el) }
                               genbruges af kurv-drawer og stoerrelsesguide.
     3) Mega-menu            — [data-nb-meganav] / [data-nb-mega] / [data-nb-mega-panel]
     4) Mobil-drawer         — [data-nb-mobmenu-open] / [data-nb-mobmenu]
                               / [data-nb-mobmenu-overlay] / [data-nb-mobmenu-close]
     5) Mobil-accordions     — [data-nb-mob-toggle]
     6) init()               — idempotent, koeres paa DOMContentLoaded
                               og paa shopify:section:load

   Regler: vanilla, ingen afhaengigheder, ingen build-step, ingen globale
   side-effects ud over window.NBModal / window.NBMenu. Al binding markeres
   med et dataset-flag, saa init() kan kaldes igen uden dobbelte listeners.
   Ingen kundevendt tekst i denne fil (den kommer fra Liquid/locales).
   ========================================================================== */

(function (window, document) {
  'use strict';

  /* ------------------------------------------------------------------ *
   * 1) Hjaelpere
   * ------------------------------------------------------------------ */

  var MOBILE_MAX = 860; /* samme breakpoint som nb-header.css */

  var FOCUSABLE = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    'iframe',
    'object',
    'embed',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function isVisible(el) {
    if (!el || el.hidden) return false;
    if (el.getAttribute && el.getAttribute('aria-hidden') === 'true') return false;
    /* offsetParent er null ved display:none (og ved position:fixed — derfor
       fallback til rects, som drawer/modal bruger). */
    if (el.offsetParent !== null) return true;
    var rects = el.getClientRects();
    return rects.length > 0;
  }

  function focusableIn(root) {
    if (!root) return [];
    return toArray(root.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return isVisible(el) && !el.closest('[hidden]');
    });
  }

  function focusFirst(root) {
    if (!root) return;
    var preferred = root.querySelector('[data-nb-autofocus]');
    var target = preferred && isVisible(preferred) ? preferred : focusableIn(root)[0];
    if (target) {
      target.focus();
      return;
    }
    if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
    root.focus();
  }

  /* --- Scroll-lock med reference-taelling ---------------------------- *
   * Flere lag kan vaere aabne samtidig (mobilmenu -> stoerrelsesguide),
   * saa laasen ophaeves foerst naar det sidste lag lukkes.
   * Vi bruger overflow:hidden (ikke position:fixed), saa den sticky
   * header ikke hopper, og kompenserer for scrollbar-bredden.        */
  var lockCount = 0;
  var lockRestore = null;

  function lockScroll() {
    lockCount += 1;
    if (lockCount > 1) return;

    var docEl = document.documentElement;
    var body = document.body;
    var scrollbar = window.innerWidth - docEl.clientWidth;

    lockRestore = {
      htmlOverflow: docEl.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPadding: body.style.paddingRight
    };

    docEl.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    if (scrollbar > 0) {
      var current = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = current + scrollbar + 'px';
    }
  }

  function unlockScroll() {
    if (lockCount === 0) return;
    lockCount -= 1;
    if (lockCount > 0 || !lockRestore) return;

    document.documentElement.style.overflow = lockRestore.htmlOverflow;
    document.body.style.overflow = lockRestore.bodyOverflow;
    document.body.style.paddingRight = lockRestore.bodyPadding;
    lockRestore = null;
  }

  /* --- Pointer-type: bruges til touch-reglen i mega-menuen ----------- */
  var lastPointerTouch = false;

  function markTouch() { lastPointerTouch = true; }
  function markMouse() { lastPointerTouch = false; }

  if (window.PointerEvent) {
    document.addEventListener('pointerdown', function (e) {
      lastPointerTouch = e.pointerType === 'touch' || e.pointerType === 'pen';
    }, true);
  } else {
    document.addEventListener('touchstart', markTouch, true);
    document.addEventListener('mousedown', markMouse, true);
  }

  function isCoarsePointer() {
    return lastPointerTouch ||
      (window.matchMedia && window.matchMedia('(hover: none)').matches);
  }

  function isMobileViewport() {
    if (window.matchMedia) {
      return window.matchMedia('(max-width: ' + MOBILE_MAX + 'px)').matches;
    }
    return window.innerWidth <= MOBILE_MAX;
  }

  /* ------------------------------------------------------------------ *
   * 2) NBModal — generisk modal/drawer-hjaelper
   *    Bruges af mobil-drawer her, og genbruges af kurv-drawer
   *    (nb-cart.js) og stoerrelsesguide (produktsiden).
   * ------------------------------------------------------------------ */

  var modalStates = new WeakMap();
  var modalStack = [];

  function resolveOverlay(el, explicit) {
    if (explicit) return explicit;
    var sel = el.getAttribute('data-nb-modal-overlay');
    return sel ? document.querySelector(sel) : null;
  }

  function trapFocus(el) {
    if (!el) return function () {};

    function onKeydown(e) {
      if (e.key !== 'Tab' && e.keyCode !== 9) return;
      var items = focusableIn(el);
      if (items.length === 0) {
        e.preventDefault();
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
        el.focus();
        return;
      }
      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || active === el || !el.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* Fokus der slipper ud (fx via adressebaren) trakkes tilbage.
       Kun det oeverste lag haandhaever — ellers ville to stablede
       modaler slaas om fokus i en uendelig loekke. */
    function onFocusIn(e) {
      if (el.contains(e.target)) return;
      var top = modalStack[modalStack.length - 1];
      if (top && top !== el) return;
      var items = focusableIn(el);
      (items[0] || el).focus();
    }

    el.addEventListener('keydown', onKeydown);
    document.addEventListener('focusin', onFocusIn);

    return function release() {
      el.removeEventListener('keydown', onKeydown);
      document.removeEventListener('focusin', onFocusIn);
    };
  }

  function openModal(el, options) {
    if (!el) return;
    var opts = options || {};
    if (modalStates.has(el)) return; /* allerede aaben */

    var state = {
      hadHidden: el.hasAttribute('hidden'),
      opener: opts.opener || (document.activeElement instanceof HTMLElement ? document.activeElement : null),
      overlay: resolveOverlay(el, opts.overlay),
      onClose: typeof opts.onClose === 'function' ? opts.onClose : null,
      closeOnOverlay: opts.closeOnOverlay !== false,
      cleanup: []
    };

    if (state.hadHidden) el.removeAttribute('hidden');
    el.classList.add('is-open');
    el.setAttribute('aria-modal', 'true');
    if (!el.hasAttribute('role')) el.setAttribute('role', 'dialog');
    el.removeAttribute('aria-hidden');
    /* Scroll inde i draweren maa ikke smitte af paa siden bagved. */
    el.style.overscrollBehavior = 'contain';

    if (state.overlay) {
      state.overlay.classList.add('is-open');
      state.overlay.removeAttribute('hidden');
      if (state.closeOnOverlay) {
        var onOverlayClick = function () { closeModal(el); };
        state.overlay.addEventListener('click', onOverlayClick);
        state.cleanup.push(function () {
          state.overlay.removeEventListener('click', onOverlayClick);
        });
      }
    }

    /* Luk-knapper inde i modalen. */
    toArray(el.querySelectorAll('[data-nb-modal-close]')).forEach(function (btn) {
      var onClick = function (e) { e.preventDefault(); closeModal(el); };
      btn.addEventListener('click', onClick);
      state.cleanup.push(function () { btn.removeEventListener('click', onClick); });
    });

    lockScroll();
    state.releaseTrap = trapFocus(el);
    modalStates.set(el, state);
    modalStack.push(el);

    /* Vent en frame, saa transitionen naar at starte foer fokus flyttes. */
    window.requestAnimationFrame(function () {
      if (modalStates.has(el)) focusFirst(el);
    });

    el.dispatchEvent(new CustomEvent('nb:modal:open', { bubbles: true }));
  }

  function closeModal(el, options) {
    if (!el) return;
    var state = modalStates.get(el);
    if (!state) return;
    var opts = options || {};

    modalStates.delete(el);
    var idx = modalStack.indexOf(el);
    if (idx > -1) modalStack.splice(idx, 1);

    if (state.releaseTrap) state.releaseTrap();
    state.cleanup.forEach(function (fn) { fn(); });

    el.classList.remove('is-open');
    el.setAttribute('aria-modal', 'false');
    if (state.hadHidden) el.setAttribute('hidden', '');
    el.style.overscrollBehavior = '';

    if (state.overlay) state.overlay.classList.remove('is-open');

    unlockScroll();

    if (state.onClose) state.onClose();

    if (opts.restoreFocus !== false && state.opener && document.contains(state.opener)) {
      state.opener.focus();
    }

    el.dispatchEvent(new CustomEvent('nb:modal:close', { bubbles: true }));
  }

  function isModalOpen(el) {
    return !!el && modalStates.has(el);
  }

  function closeTopModal() {
    if (modalStack.length === 0) return false;
    closeModal(modalStack[modalStack.length - 1]);
    return true;
  }

  /* Ryd op hvis en modal fjernes fra DOM (Section Rendering API). */
  function pruneDetachedModals() {
    modalStack.slice().forEach(function (el) {
      if (!document.contains(el)) {
        var state = modalStates.get(el);
        if (state) {
          if (state.releaseTrap) state.releaseTrap();
          state.cleanup.forEach(function (fn) { fn(); });
          if (state.overlay) state.overlay.classList.remove('is-open');
          modalStates.delete(el);
          unlockScroll();
        }
        var idx = modalStack.indexOf(el);
        if (idx > -1) modalStack.splice(idx, 1);
      }
    });
  }

  window.NBModal = {
    open: openModal,
    close: closeModal,
    trapFocus: trapFocus,
    isOpen: isModalOpen,
    closeTop: closeTopModal
  };

  /* ------------------------------------------------------------------ *
   * 3) Mega-menu
   *    Aabner paa mouseenter/focus paa nav-linket, lukker paa mouseleave
   *    af HELE nav-blokken og paa Escape. Kun én aaben ad gangen.
   * ------------------------------------------------------------------ */

  var megaNavs = [];

  function createMegaNav(root) {
    var current = null; /* aktivt trigger-element */

    function panelFor(trigger) {
      var id = trigger.getAttribute('data-nb-mega');
      return id ? document.getElementById(id) : null;
    }

    function closeAll(opts) {
      var restoreFocus = opts && opts.restoreFocus;
      var previous = current;
      current = null;
      toArray(root.querySelectorAll('[data-nb-mega]')).forEach(function (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        var panel = panelFor(trigger);
        if (panel) panel.hidden = true;
      });
      if (restoreFocus && previous && document.contains(previous)) previous.focus();
    }

    function open(trigger) {
      if (!trigger) return;
      var panel = panelFor(trigger);
      if (!panel) return;
      if (current === trigger) return;
      closeAll();
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      current = trigger;
    }

    /* --- Mus: aabn paa hover af trigger, luk paa hover af andre links -- */
    root.addEventListener('mouseover', function (e) {
      if (isCoarsePointer()) return;
      var target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      if (target.closest('[data-nb-mega-panel]')) return; /* hold panelet aabent */
      var trigger = target.closest('[data-nb-mega]');
      if (trigger) {
        open(trigger);
      } else if (target.closest('.nb-navlink')) {
        closeAll();
      }
    });

    /* --- Luk paa mouseleave af HELE nav-blokken (ikke pr. link) -------- */
    root.addEventListener('mouseleave', function () {
      closeAll();
    });

    /* --- Tastatur: fokus paa trigger aabner, fokus udenfor lukker ------ */
    root.addEventListener('focusin', function (e) {
      var target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      if (target.closest('[data-nb-mega-panel]')) return;
      var trigger = target.closest('[data-nb-mega]');
      if (trigger) open(trigger);
      else closeAll();
    });

    root.addEventListener('focusout', function () {
      window.setTimeout(function () {
        if (!root.contains(document.activeElement)) closeAll();
      }, 0);
    });

    root.addEventListener('keydown', function (e) {
      var target = e.target instanceof Element ? e.target : null;
      if (!target) return;

      if (e.key === 'Escape' || e.key === 'Esc') {
        if (current) {
          e.stopPropagation();
          closeAll({ restoreFocus: true });
        }
        return;
      }

      var trigger = target.closest('[data-nb-mega]');
      if (trigger && (e.key === 'ArrowDown' || e.key === 'Down')) {
        e.preventDefault();
        open(trigger);
        var panel = panelFor(trigger);
        var first = focusableIn(panel)[0];
        if (first) first.focus();
      }
    });

    /* --- Touch: foerste tap aabner panelet i stedet for at navigere ---- */
    root.addEventListener('click', function (e) {
      var target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      var trigger = target.closest('[data-nb-mega]');
      if (!trigger) return;
      if (!isCoarsePointer()) return;      /* mus: linket navigerer som normalt */
      if (current === trigger) return;     /* andet tap: lad linket navigere */
      e.preventDefault();
      open(trigger);
    });

    /* --- Klik udenfor nav-blokken lukker ------------------------------ */
    var onDocClick = function (e) {
      var target = e.target instanceof Element ? e.target : null;
      if (!target || !root.contains(target)) closeAll();
    };
    document.addEventListener('click', onDocClick);

    return {
      root: root,
      close: function () { closeAll(); },
      isOpen: function () { return current !== null; },
      destroy: function () { document.removeEventListener('click', onDocClick); }
    };
  }

  function initMega(scope) {
    toArray(scope.querySelectorAll('[data-nb-meganav]')).forEach(function (root) {
      if (root.dataset.nbMegaBound === 'true') return;
      root.dataset.nbMegaBound = 'true';
      megaNavs.push(createMegaNav(root));
    });
    /* Ryd referencer til nav-blokke der er fjernet fra DOM. */
    megaNavs = megaNavs.filter(function (nav) {
      if (document.contains(nav.root)) return true;
      nav.destroy();
      return false;
    });
  }

  function closeAllMega() {
    megaNavs.forEach(function (nav) { nav.close(); });
  }

  /* ------------------------------------------------------------------ *
   * 4) Mobil-drawer
   * ------------------------------------------------------------------ */

  function collapseMobileAccordions(drawer) {
    toArray(drawer.querySelectorAll('[data-nb-mob-toggle]')).forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
      var id = btn.getAttribute('aria-controls');
      var panel = id ? document.getElementById(id) : null;
      if (panel) panel.hidden = true;
    });
  }

  function initMobileDrawer(scope) {
    var drawer = scope.querySelector('[data-nb-mobmenu]') ||
                 document.querySelector('[data-nb-mobmenu]');
    if (!drawer || drawer.dataset.nbMenuBound === 'true') return;
    drawer.dataset.nbMenuBound = 'true';

    var overlay = document.querySelector('[data-nb-mobmenu-overlay]');
    var openers = toArray(document.querySelectorAll('[data-nb-mobmenu-open]'));

    function setOpenersExpanded(value) {
      openers.forEach(function (btn) {
        btn.setAttribute('aria-expanded', value ? 'true' : 'false');
      });
    }

    function openDrawer(opener) {
      if (isModalOpen(drawer)) return;
      closeAllMega();
      setOpenersExpanded(true);
      openModal(drawer, {
        overlay: overlay,
        opener: opener,
        onClose: function () {
          setOpenersExpanded(false);
          collapseMobileAccordions(drawer);
        }
      });
    }

    function closeDrawer() {
      closeModal(drawer);
    }

    openers.forEach(function (btn) {
      if (btn.dataset.nbMenuBound === 'true') return;
      btn.dataset.nbMenuBound = 'true';
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (isModalOpen(drawer)) closeDrawer();
        else openDrawer(btn);
      });
    });

    toArray(drawer.querySelectorAll('[data-nb-mobmenu-close]')).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        closeDrawer();
      });
    });

    if (overlay && overlay.dataset.nbMenuBound !== 'true') {
      overlay.dataset.nbMenuBound = 'true';
      overlay.addEventListener('click', function () {
        if (isModalOpen(drawer)) closeDrawer();
      });
    }

    /* Klik paa et link i draweren lukker den (ogsaa ved anker-links). */
    drawer.addEventListener('click', function (e) {
      var target = e.target instanceof Element ? e.target : null;
      var link = target ? target.closest('a[href]') : null;
      if (link && drawer.contains(link)) closeDrawer();
    });

    /* Accordions i draweren: én aaben ad gangen (som i prototypen). */
    drawer.addEventListener('click', function (e) {
      var target = e.target instanceof Element ? e.target : null;
      var toggle = target ? target.closest('[data-nb-mob-toggle]') : null;
      if (!toggle || !drawer.contains(toggle)) return;
      e.preventDefault();

      var id = toggle.getAttribute('aria-controls');
      var panel = id ? document.getElementById(id) : null;
      var wasOpen = toggle.getAttribute('aria-expanded') === 'true';

      collapseMobileAccordions(drawer);

      if (!wasOpen) {
        toggle.setAttribute('aria-expanded', 'true');
        if (panel) panel.hidden = false;
      }
    });

    /* Skifter viewport til desktop, lukkes draweren. */
    var onResize = function () {
      if (isModalOpen(drawer) && !isMobileViewport()) {
        closeModal(drawer, { restoreFocus: false });
      }
    };
    if (window.matchMedia) {
      var mq = window.matchMedia('(max-width: ' + MOBILE_MAX + 'px)');
      if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onResize);
      else if (typeof mq.addListener === 'function') mq.addListener(onResize);
    } else {
      window.addEventListener('resize', onResize);
    }
  }

  /* ------------------------------------------------------------------ *
   * 5) Escape paa dokument-niveau
   *    Lukker oeverste modal, ellers en aaben mega-menu.
   * ------------------------------------------------------------------ */

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc' && e.keyCode !== 27) return;
    if (closeTopModal()) return;
    closeAllMega();
  });

  /* ------------------------------------------------------------------ *
   * 6) init — idempotent
   * ------------------------------------------------------------------ */

  function init(scope) {
    var root = (scope && typeof scope.querySelectorAll === 'function') ? scope : document;
    pruneDetachedModals();
    initMega(root);
    initMobileDrawer(root);
  }

  window.NBMenu = {
    init: init,
    closeMega: closeAllMega
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Section Rendering API / theme editor. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });

  document.addEventListener('shopify:section:unload', function () {
    pruneDetachedModals();
    closeAllMega();
  });

}(window, document));
